import * as React from 'react';
import type { User } from '../types';
import CloseIcon from './icons/CloseIcon';
import SearchIcon from './icons/SearchIcon';

const { useState, useEffect, useRef } = React;

interface UserMultiSelectProps {
    label: string;
    placeholder: string;
    availableUsers: User[];
    selectedUsers: User[];
    onSelectionChange: (users: User[]) => void;
}

const UserMultiSelect: React.FC<UserMultiSelectProps> = ({ label, placeholder, availableUsers, selectedUsers, onSelectionChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggleUser = (user: User) => {
        const isSelected = selectedUsers.some(su => su.handle === user.handle);
        if (isSelected) {
            onSelectionChange(selectedUsers.filter(su => su.handle !== user.handle));
        } else {
            onSelectionChange([...selectedUsers, user]);
        }
    };

    const filteredUsers = availableUsers.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.handle.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div ref={wrapperRef}>
            {label && <label className="block text-sm font-medium text-brand-muted mb-1">{label}</label>}
            <div className="relative">
                <div onClick={() => setIsOpen(!isOpen)} className="w-full bg-gray-700 text-white rounded-md p-2 flex flex-wrap gap-2 items-center cursor-pointer min-h-[50px]">
                    {selectedUsers.length === 0 && <span className="text-gray-400 pl-2">{placeholder}</span>}
                    {selectedUsers.map(user => (
                        <span key={user.handle} className="bg-brand-orange/50 text-white px-2 py-1 rounded-full text-sm flex items-center gap-2">
                            {user.name}
                            <button onClick={(e) => { e.stopPropagation(); handleToggleUser(user); }} className="bg-white/20 hover:bg-white/40 rounded-full"><CloseIcon className="h-3 w-3" /></button>
                        </span>
                    ))}
                </div>
                {isOpen && (
                    <div className="absolute top-full mt-2 w-full bg-brand-surface border border-gray-600 rounded-md shadow-lg z-10 p-2">
                        <div className="relative mb-2"><input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search users..." className="w-full bg-gray-700 text-white rounded-md p-2 pl-8 focus:outline-none"/><SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" /></div>
                        <ul className="max-h-48 overflow-y-auto">
                            {filteredUsers.map(user => (
                                <li key={user.handle} className="p-2 hover:bg-gray-700 rounded-md cursor-pointer flex items-center" onClick={() => handleToggleUser(user)}>
                                    <img src={user.avatarUrl} alt={user.name} className="h-8 w-8 rounded-full mr-3"/>
                                    <div><p className="font-semibold">{user.name}</p><p className="text-xs text-brand-muted">@{user.handle}</p></div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserMultiSelect;